import React from 'react';
import * as Yup from 'yup';
import { FilterList } from '@mui/icons-material';
import { Badge, Box, Divider, Typography } from '@mui/material';
import { CancelButton, CheckboxGroup, createFilters, DropdownFilterBlock, DropdownFilterButton, ModalActions, ModalTitle, SubmitButton } from '@uplift/ui-v2';
import { Formik } from 'formik';
import { ActivityAppointmentStatusFilter, ActivityItemCategory, AppointmentType } from '@uplift/api';
import { DateObject } from '@uplift/services';


type ActivitiesFiltersType = {
  activityCategories: ActivityItemCategory[];
  appointmentTypes: AppointmentType[];
  appointmentStatus: ActivityAppointmentStatusFilter[];
  fromDate: DateObject | null;
  toDate: DateObject | null;
};


const useFiltersValidationSchema = (dateFilterOpen: boolean) =>
  Yup.object().shape({
    fromDate: dateFilterOpen
      ? Yup.mixed<DateObject>()
        .nullable()
        .test(
          'is-before-toDate',
          'This date should be before To Date',
          function (value) {
            const { toDate } = this.parent;
            const isValid = toDate ? value < toDate : true;

            return isValid;
          }
        )
      : Yup.mixed<DateObject>().nullable(),
    toDate: dateFilterOpen
      ? Yup.mixed<DateObject>()
        .nullable()
        .test(
          'is-after-fromDate',
          'This date should be after From Date',
          function (value) {
            const { fromDate } = this.parent;
            const isValid = fromDate ? value > fromDate : true;

            return isValid;
          }
        )
      : Yup.mixed<DateObject>().nullable()
  });


export const ActivitiesFiltersContext = createFilters<null, ActivitiesFiltersType>({
  initialValues: {
    activityCategories: [],
    appointmentTypes: [],
    appointmentStatus: [],
    fromDate: null,
    toDate: null,
  },
  calcFiltersCount(obj) {
    return [
      obj.activityCategories.length > 0,
      obj.appointmentTypes.length > 0,
      obj.appointmentStatus.length > 0,
      !!(obj.fromDate && obj.toDate),
    ].reduce((acc, v) => acc + (v ? 1 : 0), 0);
  },
});


export const ActivitiesFilters: React.VFC = () => {
  const [ open, setOpen ] = React.useState(false);
  const filtersContext = ActivitiesFiltersContext.useContext();
  const filtersValidationSchema = useFiltersValidationSchema(open);

  return (
    <DropdownFilterButton
      open={open}
      setOpen={setOpen}
      label=""
      startIcon={(
        <Box display="flex" flexDirection="row" gap="8px">
          <Badge badgeContent={filtersContext.filtersCount} color="secondary">
            <FilterList />
          </Badge>
          <Typography>Filters</Typography>
        </Box>
      )}
    >
      <Formik
        initialValues={filtersContext.filters}
        validationSchema={filtersValidationSchema}
        onSubmit={(values) => {
          filtersContext.setFilterValues(values);
          setOpen(false);
        }}
      >
        {formik => (
          <Box width="400px" display="flex" flexDirection="column">
            <ModalTitle onClose={() => setOpen(false)}>Filters</ModalTitle>
            <Divider />
            <Box maxHeight="425px" overflow="auto">
              <DropdownFilterBlock
                title="Service Type"
                onReset={() => formik.setValues({ ...formik.values, appointmentTypes: [] })}
                hasValue={formik.values.appointmentTypes.length > 0}
              >
                <CheckboxGroup
                  name="appointmentTypes"
                  options={[
                    { value: AppointmentType.therapy, label: 'Therapy' },
                    { value: AppointmentType.psychiatry, label: 'Psychiatry' },
                  ]}
                />
              </DropdownFilterBlock>
              <Divider />
              <DropdownFilterBlock
                title="Appointment Status"
                onReset={() => formik.setValues({ ...formik.values, appointmentStatus: [] })}
                hasValue={formik.values.appointmentStatus.length > 0}
              >
                <CheckboxGroup
                  name="appointmentStatus"
                  options={[
                    { value: ActivityAppointmentStatusFilter.submited, label: 'Submitted' },
                    { value: ActivityAppointmentStatusFilter.pendingSubmission, label: 'Pending submission' },
                    // { value: ActivityAppointmentStatusFilter.canceled, label: 'Cancelled' },
                  ]}
                />
              </DropdownFilterBlock>
              {/*
              <Divider />
              <DropdownFilterBlock
                title="Date of Service Range"
                onReset={() => {
                  formik.setValues({ ...formik.values, fromDate: null, toDate: null });
                }}
                hasValue={(!!formik.values.fromDate && !!formik.values.toDate)}
                contentPadding={false}
              >
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" pt={1}>
                  <DatePicker
                    name="fromDate"
                    helperText="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                  />
                  <Box px="8px">
                    <Typography lineHeight="48px">–</Typography>
                  </Box>
                  <DatePicker
                    name="toDate"
                    helperText="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                  />
                </Box>
              </DropdownFilterBlock> */}
              <Divider />
              <DropdownFilterBlock
                title="Activity Categories"
                onReset={() => formik.setValues({ ...formik.values, activityCategories: [] })}
                hasValue={formik.values.activityCategories.length > 0}
              >
                <CheckboxGroup
                  name="activityCategories"
                  options={[
                    { value: ActivityItemCategory.appointments, label: 'Appointments' },
                    { value: ActivityItemCategory.rematch, label: 'Rematch' },
                    { value: ActivityItemCategory.newMatches, label: 'New Matches' },
                    { value: ActivityItemCategory.allForms, label: 'All Forms' },
                    { value: ActivityItemCategory.documents, label: 'Documents' },
                    { value: ActivityItemCategory.notes, label: 'Notes' },
                    { value: ActivityItemCategory.referrals, label: 'Referrals' },
                    { value: ActivityItemCategory.messages, label: 'Messages' },
                  ]}
                />
              </DropdownFilterBlock>
            </Box>
            <Divider />
            <ModalActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <CancelButton
                color="secondary"
                size="small"
                onClick={() => {
                  formik.setValues({
                    activityCategories: [],
                    appointmentTypes: [],
                    appointmentStatus: [],
                    fromDate: null,
                    toDate: null
                  });
                  formik.handleSubmit();
                }}
              >Reset all</CancelButton>
              <SubmitButton color="secondary" size="small">Apply</SubmitButton>
            </ModalActions>
          </Box>
        )}

      </Formik>
    </DropdownFilterButton>
  );
};
